import { ValueSkeleton } from '@shared/ui/skeleton';
import type { CSSProperties } from 'react';

import type { CampaignRewards as Rewards } from '../model/campaign-rewards';
import { ResourceRewards, RewardSummary } from '@entities/game-resource';

import classes from './campaign-progress.module.css';

type CampaignProgressProps = {
  completedChapters: number;
  totalChapters: number;
  earnedCoins?: number;
  totalCoins?: number;
  rewards?: Rewards;
  earnedRewards?: Rewards;
  showCompletionRewards?: boolean;
  compact?: boolean;
  loading?: boolean;
  className?: string;
  ariaLabel?: string;
};

export const CampaignProgress = ({
  completedChapters,
  totalChapters,
  earnedCoins,
  totalCoins,
  rewards,
  earnedRewards,
  showCompletionRewards = true,
  compact = false,
  loading = false,
  className,
  ariaLabel = 'Пройдено глав кампании',
}: CampaignProgressProps) => {
  if (loading)
    return (
      <span
        className={`${classes.root} ${className ?? ''}`}
        data-compact={compact || undefined}
        role="status"
        aria-label="Загружаем прогресс кампании"
        aria-busy="true"
      >
        <span className={classes.meta}>
          <span className={classes.chapters}>
            <span className={classes.label}>Главы</span>
            <ValueSkeleton width={44} />
          </span>
          <span className={classes.reward}>
            <span className={classes.label}>XP</span>
            <ValueSkeleton width={44} />
          </span>
        </span>
        <span className={classes.reward}>
          <img src="/uznabuck.webp" alt="Монеты" />
          <ValueSkeleton width={44} />
        </span>
        <span className={classes.track} aria-hidden="true" />
      </span>
    );
  const percentage = totalChapters ? Math.min(100, (completedChapters / totalChapters) * 100) : 0;

  return (
    <span className={`${classes.root} ${className ?? ''}`} data-compact={compact || undefined}>
      <span className={classes.meta}>
        <span className={classes.chapters}>
          <span className={classes.label}>Главы</span>
          <strong data-complete={(totalChapters > 0 && completedChapters >= totalChapters) || undefined}>
            <span className={classes.current}>{completedChapters}</span> <span className={classes.separator}>/</span>{' '}
            {totalChapters}
          </strong>
        </span>
        {rewards && <RewardSummary rewards={rewards} earned={earnedRewards} only="experience" />}
      </span>
      <span className={classes.rewards}>
        {rewards ? (
          <>
            <RewardSummary rewards={rewards} earned={earnedRewards} only="coins" />
            {showCompletionRewards && <ResourceRewards rewards={rewards} />}
          </>
        ) : (
          earnedCoins !== undefined &&
          totalCoins !== undefined && (
            <span
              className={classes.reward}
              role="group"
              aria-label={`Заработано ${earnedCoins} из ${totalCoins} узнабаксов`}
            >
              <img src="/uznabuck.webp" alt="" aria-hidden="true" />
              <strong data-complete={(totalCoins > 0 && earnedCoins >= totalCoins) || undefined}>
                <span className={classes.current}>{earnedCoins}</span> <span className={classes.separator}>/</span>{' '}
                {totalCoins}
              </strong>
            </span>
          )
        )}
      </span>
      <span
        className={classes.track}
        role="progressbar"
        aria-label={ariaLabel}
        aria-valuemin={0}
        aria-valuemax={totalChapters}
        aria-valuenow={completedChapters}
        style={{ '--segment-size': `${100 / Math.max(totalChapters, 1)}%` } as CSSProperties}
      >
        <span style={{ width: `${percentage}%` }} />
      </span>
    </span>
  );
};
